import { useEffect, useState } from 'react'
import { ChevronRight, Heart } from 'lucide-react'
import type { CastPortrait } from './castPortraits'
import type { CastProfile } from './castProfiles'
import { timelineForPerson } from './castProfileTimelines'
import { buildCatalogProfilePresentation } from './catalogProfilePresentation'
import { whereItStartedForProfile } from './profileRelationshipStatus'
import type { PersonProfilePayload } from './profileTypes'
import { useSocialPreview } from './useTikTokPreview'

type CastProfileHeroProps = {
  profile?: CastProfile
  portrait?: CastPortrait
  record: PersonProfilePayload | null
  displayName: string
  initials: string
  onOpenPerson: (personId: string, name: string) => void
}

function formatStatusDate(value: string | null | undefined) {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null
  const date = new Date(`${value}T00:00:00Z`)
  if (Number.isNaN(date.getTime())) return null
  return new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' }).format(date)
}

export default function CastProfileHero({ profile, portrait, record, displayName, initials, onOpenPerson }: CastProfileHeroProps) {
  const generated = buildCatalogProfilePresentation(record, displayName)
  const preview = useSocialPreview(portrait?.postUrl ?? '', portrait?.platform ?? 'tiktok', Boolean(portrait))
  const [imageFailed, setImageFailed] = useState(false)
  useEffect(() => setImageFailed(false), [preview.thumbnailUrl])
  const hasPhoto = Boolean(portrait) && preview.status === 'ready' && Boolean(preview.thumbnailUrl) && !imageFailed
  const timeline = profile ? timelineForPerson(profile.personId) : undefined
  const origin = whereItStartedForProfile(record, timeline)

  const statusDate = formatStatusDate(origin?.statusAsOf)
  const statusHeading = profile?.entryKind === 'story_identity' ? 'Identity status' : timeline?.origin?.connectionKind === 'family' || timeline?.origin?.connectionKind === 'friend' ? 'Connection status' : 'Relationship status'

  return (
    <div className={`entity-hero entity-hero--person cast-profile-hero ${hasPhoto ? 'has-photo' : ''} ${origin ? 'has-origin' : ''}`}>
      {hasPhoto && portrait && (
        <img
          className="cast-profile-hero__photo"
          src={preview.thumbnailUrl}
          alt={portrait.accessibilitySummary}
          referrerPolicy="no-referrer"
          style={{ objectPosition: `center ${portrait.focusY ?? 50}%` }}
          onError={() => setImageFailed(true)}
        />
      )}
      <span className="cast-profile-hero__shade" />
      <span className="entity-monogram">{portrait?.initials ?? initials}</span>
      <div className="cast-profile-hero__copy">
        <span className="eyebrow">{profile?.entryKind === 'story_identity' ? 'STORY IDENTITY' : 'CAST PROFILE'}</span>
        <h2>{portrait?.name ?? record?.person.name ?? displayName}</h2>
        <p>{profile?.headline ?? generated.headline}</p>
        {origin && (
          <button
            type="button"
            className="cast-profile-hero__origin"
            disabled={!origin.partner.person_id}
            onClick={() => onOpenPerson(origin.partner.person_id, origin.partner.name)}
            aria-label={`Where it started: ${origin.introLabel ?? `first seen with ${origin.partner.name}`}. ${statusHeading}: ${origin.statusLabel}.${origin.partner.person_id ? ` Open ${origin.partner.name}'s cast profile.` : ''}`}
          >
            <span className="cast-profile-hero__origin-icon"><Heart size={15} strokeWidth={2.5} /></span>
            <span className="cast-profile-hero__origin-story">
              <small>WHERE IT STARTED</small>
              <strong>{origin.introLabel ?? `First seen with ${origin.partner.name}`}</strong>
              {(origin.show || origin.season) && (
                <em>{origin.show}{origin.season ? ` · Season ${origin.season}` : ''}</em>
              )}
            </span>
            <span className={`cast-profile-hero__origin-status cast-profile-hero__origin-status--${origin.statusTone}`}>
              <small>{statusHeading.toUpperCase()}</small>
              <strong>{origin.statusLabel}</strong>
              {statusDate && <em>Verified {statusDate}</em>}
            </span>
            {origin.partner.person_id && <ChevronRight size={16} strokeWidth={2.5} />}
          </button>
        )}
      </div>
    </div>
  )
}
